import React, { useEffect, useState } from 'react';
import {
  Card, CardHeader, CardContent, Grid, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, Alert,
  TableRow, TablePagination, IconButton, Snackbar, Dialog, InputLabel,
  DialogTitle, DialogContent, DialogActions, MenuItem, FormControl, Select, Box,
  Divider, Typography, Avatar, InputAdornment, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import Swal from 'sweetalert2';

const apiUrl = 'https://apidashboard.eurekagroup.id/joblevel';
const addUrl = `${apiUrl}/add`;
const editUrl = `${apiUrl}/edit`;
const deleteUrl = (id) => `${apiUrl}/delete/${id}`;

const JoblevelPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJobLevel, setSelectedJobLevel] = useState(null);
  const [joblevelCode, setJoblevelCode] = useState('');
  const [joblevelName, setJoblevelName] = useState('');
  const [idBu, setIdBu] = useState([]);
  const [isActive, setIsActive] = useState(1);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [errors, setErrors] = useState({ joblevelCode: '', joblevelName: '', idBu: '' });

  useEffect(() => {
    const fetchJobLevels = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching job level data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchBusinessUnits = async () => {
      try {
        const response = await fetch('https://apidashboard.eurekagroup.id/bu');
        const data = await response.json();
        setBusinessUnits(data);
      } catch (error) {
        console.error("Error fetching Business Units:", error);
      }
    };

    fetchJobLevels();
    fetchBusinessUnits();
  }, []);

  const handleSearchChange = (event) => setSearchText(event.target.value);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (joblevel) => {
    if (joblevel) {
      setSelectedJobLevel(joblevel);
      setJoblevelCode(joblevel.joblevel_code);
      setJoblevelName(joblevel.joblevel_name);
      setIdBu(Array.isArray(joblevel.id_bu) ? joblevel.id_bu.map(bu => bu._id) : []);
      setIsActive(joblevel.is_active);
    } else {
      setSelectedJobLevel(null);
      setJoblevelCode('');
      setJoblevelName('');
      setIdBu([]);
      setIsActive(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSaveJobLevel = async () => {
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    const newErrors = {};
    if (!joblevelCode) newErrors.joblevelCode = 'Job Level Code is required';
    if (!joblevelName) newErrors.joblevelName = 'Job Level Name is required';
    if (idBu.length === 0) newErrors.idBu = 'Business Unit is required';
  
    setErrors(newErrors);
  
    if (Object.keys(newErrors).length > 0) return;
  
    const joblevelData = { joblevel_code: joblevelCode, joblevel_name: joblevelName, id_bu: idBu, is_active: isActive };
  
    setLoading(true); // Start loading
  
    const fetchJobLevels = async () => {
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching job level data:', error);
      }
    };
  
    if (selectedJobLevel) {
      try {
        await axios.put(editUrl, { ...joblevelData, id: selectedJobLevel._id }, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData(data.map((jl) => (jl._id === selectedJobLevel._id ? { ...jl, ...joblevelData } : jl)));
        setMessage('Job Level updated successfully');
        Swal.fire('Success', 'Job Level updated successfully', 'success');
      } catch (error) {
        console.error('Error updating job level:', error);
        Swal.fire('Error', 'Failed to update Job Level', 'error');
      }
    } else {
      try {
        const response = await axios.post(addUrl, joblevelData, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData([response.data, ...data]); // Add new job level to the data state
        setMessage('Job Level added successfully');
        Swal.fire('Success', 'Job Level added successfully', 'success');
      } catch (error) {
        console.error('Error adding job level:', error);
        Swal.fire('Error', 'Failed to add Job Level', 'error');
      }
    }
  
    setLoading(false); // Stop loading
    handleCloseDialog();
    await fetchJobLevels();  // Re-fetch data after save
  };  

  const handleDeleteClick = async (id) => {
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      setLoading(true); // Start loading
      try {
        await axios.delete(deleteUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData(data.filter((jl) => jl._id !== id));
        setMessage('Job Level deleted successfully');
        Swal.fire('Success', 'Job Level deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting job level:', error);
        Swal.fire('Error', 'Failed to delete Job Level', 'error');
      }
      setLoading(false); // Stop loading
    }
  };  

  const filteredData = data.filter((jl) => jl.joblevel_name.toLowerCase().includes(searchText.toLowerCase()));

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader
              title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <TrendingUpIcon />
              </Avatar>
                  <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Level Jabatan
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola level jabatan dengan mudah dan efisien
                </Typography>
                  </Box>
                </Box>
              }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Level Jabatan
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
            <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Level Jabatan"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Level Jabatan
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data level jabatan...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kode Level Jabatan
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Level Jabatan
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Unit Bisnis
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((jl, index) => (
                      <TableRow 
                        key={jl._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {jl.joblevel_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {jl.joblevel_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 200 }}>
                        {jl.id_bu && Array.isArray(jl.id_bu) && jl.id_bu.length > 0 ? (
                              jl.id_bu.slice(0, 3).map((bu, buIndex) => (
                                <Box key={buIndex} sx={{ 
                                  display: 'inline-block',
                                  px: 1,
                                  py: 0.5,
                                  mr: 0.5,
                                  mb: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'primary.light',
                                  color: 'primary.dark',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}>
                                  {bu.bu_name || 'N/A'}
                                </Box>
                          ))
                        ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                N/A
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: jl.is_active === 1 ? 'success.light' : 'error.light',
                            color: jl.is_active === 1 ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {jl.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                      </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(jl)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                          </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(jl._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
            </CardContent>
          </Card>
    </div>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{selectedJobLevel ? 'Edit Job Level' : 'Add Job Level'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Job Level Code"
            fullWidth
            value={joblevelCode}
            onChange={(e) => setJoblevelCode(e.target.value)}
            sx={{ marginTop: 2 }}
            required
            error={Boolean(errors.joblevelCode)}
            helperText={errors.joblevelCode}
          />
          <TextField
            label="Job Level Name"
            fullWidth
            value={joblevelName}
            onChange={(e) => setJoblevelName(e.target.value)}
            sx={{ marginTop: 2 }}
            required
            error={Boolean(errors.joblevelName)}
            helperText={errors.joblevelName}
          />
          <FormControl fullWidth sx={{ marginTop: 2 }} required>
            <InputLabel>Business Unit</InputLabel>
            <Select
              multiple
              value={idBu}
              onChange={(e) => setIdBu(e.target.value)}
              label="Business Unit"
              error={Boolean(errors.idBu)}
            >
              {businessUnits.map((bu) => (
                <MenuItem key={bu._id} value={bu._id}>
                  {bu.bu_name}
                </MenuItem>
              ))}
            </Select>
            {errors.idBu && <Box color="error.main" sx={{ fontSize: '0.875rem' }}>{errors.idBu}</Box>}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveJobLevel} disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>

      {message && (
        <Snackbar
          open={Boolean(message)}
          autoHideDuration={6000}
          onClose={() => setMessage('')}
        >
          <Alert severity="success" onClose={() => setMessage('')}>
            {message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default JoblevelPage;
